import { useState } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { KeyRound, ShieldCheck } from "lucide-react";

import MainLayout from "@/components/MainLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { authApi } from "@/lib/api";

const demoAccounts = [
  {
    label: "Mitgliedsdemo",
    email: "max.hansen@reexperts.de",
    password: "Rexpert2026!",
    note: "Mitgliedsakte mit Kennung, Fortbildungsstand, Vorgängen und Beitragsführung.",
  },
];

export default function LoginPage({ session, onLogout, onLogin }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const [resetForm, setResetForm] = useState({ email: "", code: "", new_password: "" });
  const { register, handleSubmit, setValue } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const submitLogin = async (values) => {
    setLoading(true);
    try {
      const response = await authApi.login(values);
      onLogin(response);
      toast.success("Zugang erfolgreich bestätigt.");
      navigate(response.user.role === "admin" ? "/admin" : "/mitglied");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Login fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const requestReset = async () => {
    if (!resetForm.email) {
      toast.error("Bitte eine registrierte E-Mail-Adresse eingeben.");
      return;
    }
    setResetLoading(true);
    try {
      await authApi.requestPasswordReset({ email: resetForm.email });
      toast.success("Falls die Adresse registriert ist, wurde ein Einmalcode versendet.");
      setResetStep(2);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Die Passwort-Zurücksetzung konnte nicht gestartet werden.");
    } finally {
      setResetLoading(false);
    }
  };

  const confirmReset = async () => {
    if (!resetForm.code || !resetForm.new_password) {
      toast.error("Bitte Einmalcode und neues Passwort vollständig eingeben.");
      return;
    }
    setResetLoading(true);
    try {
      await authApi.confirmPasswordReset(resetForm);
      toast.success("Das Passwort wurde erfolgreich zurückgesetzt. Sie können sich nun mit dem neuen Passwort anmelden.");
      setShowReset(false);
      setResetStep(1);
      setResetForm({ email: "", code: "", new_password: "" });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Das Passwort konnte nicht zurückgesetzt werden.");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <MainLayout
      session={session}
      onLogout={onLogout}
      pageTitle="Mitglieder- und Verwaltungszugang"
      pageDescription="Login für Mitglieder und Verbandsverwaltung von REEXPERTS."
    >
      <div className="mx-auto max-w-6xl px-6 py-12 lg:px-10 lg:py-20">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="grid gap-8 lg:grid-cols-[0.92fr_1.08fr]">
          <div className="space-y-6 rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm" data-testid="login-info-panel">
            <Badge className="w-fit rounded-full border border-slate-200 bg-slate-100 text-slate-700" data-testid="login-badge">
              <span className="mr-3 inline-flex items-center gap-1">
                <span className="h-2 w-4 rounded-full bg-[#2f2f2f]" />
                <span className="h-2 w-4 rounded-full bg-[#a8171b]" />
                <span className="h-2 w-4 rounded-full bg-[#d4af37]" />
              </span>
              <ShieldCheck className="mr-2 h-4 w-4" /> Zugriff auf Mitglieds- und Adminbereiche
            </Badge>
            <h1 className="font-serif text-[1.95rem] leading-tight tracking-tight text-slate-900 sm:text-3xl" data-testid="login-heading">
              Zugang zum Mitglieder- und Verwaltungsbereich der Vereinigung.
            </h1>
            <p className="text-base leading-relaxed text-slate-700" data-testid="login-description">
              Über den Zugang gelangen Sie entweder in die Mitgliedsakte mit Kennung, Fortbildungsstand und Beitragsstatus oder in die vollständige Verwaltungsoberfläche mit Prüf-, Rechts- und Freigabeprozessen.
            </p>
            <p className="text-sm uppercase tracking-[0.22em] text-[#8a6f3b]" data-testid="login-dach-note">
              Klare Zugänge für Mitglieder und Verwaltung im D-A-CH-Verbandsrahmen.
            </p>

            <div className="grid gap-4">
              {demoAccounts.map((account) => (
                <Card key={account.label} className="border-slate-200 bg-slate-50 shadow-none" data-testid={`login-demo-card-${account.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <CardContent className="space-y-3 p-5">
                    <div className="flex items-center gap-2" data-testid={`login-demo-card-flag-${account.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <span className="h-[3px] w-8 rounded-full bg-[#2f2f2f]" />
                      <span className="h-[3px] w-8 rounded-full bg-[#a8171b]" />
                      <span className="h-[3px] w-8 rounded-full bg-[#d4af37]" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{account.label}</p>
                      <p className="text-sm text-slate-600">{account.note}</p>
                    </div>
                    <div className="rounded-3xl bg-white p-4 text-sm text-slate-700">
                      <p data-testid={`login-demo-email-${account.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>E-Mail: {account.email}</p>
                      <p data-testid={`login-demo-password-${account.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>Passwort: {account.password}</p>
                    </div>
                    <Button
                      variant="outline"
                      className="rounded-full border-white/10 bg-transparent text-white hover:bg-white/5"
                      onClick={() => {
                        setValue("email", account.email);
                        setValue("password", account.password);
                      }}
                      data-testid={`login-use-demo-button-${account.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      Demo-Zugang übernehmen
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit(submitLogin)} className="space-y-6 rounded-[2rem] border border-[#d4af37]/25 bg-slate-950 p-8 text-white shadow-sm" data-testid="login-form-panel">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-slate-400" data-testid="login-form-kicker">Sicherer Zugang</p>
              <div className="mt-3 flex items-center gap-2" data-testid="login-form-flag-accent">
                <span className="h-[3px] w-8 rounded-full bg-[#2f2f2f]" />
                <span className="h-[3px] w-8 rounded-full bg-[#a8171b]" />
                <span className="h-[3px] w-8 rounded-full bg-[#d4af37]" />
              </div>
              <h2 className="mt-3 font-serif text-[2rem] tracking-tight" data-testid="login-form-heading">Zugangsdaten eingeben</h2>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">E-Mail</label>
              <Input type="email" {...register("email", { required: true })} className="border-white/10 bg-white/5 text-white" data-testid="login-email-input" />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">Passwort</label>
              <Input type="password" {...register("password", { required: true })} className="border-white/10 bg-white/5 text-white" data-testid="login-password-input" />
            </div>
            <button type="button" className="text-sm text-slate-300 underline underline-offset-4" onClick={() => setShowReset((current) => !current)} data-testid="login-forgot-password-button">
              Passwort vergessen?
            </button>
            {showReset && (
              <div className="space-y-4 rounded-3xl border border-white/10 bg-white/5 p-5" data-testid="login-reset-panel">
                <p className="font-semibold text-white">Passwort-Zurücksetzung für registrierte Mitglieder</p>
                <p className="text-sm leading-relaxed text-slate-300" data-testid="login-reset-description">
                  Geben Sie zunächst Ihre registrierte E-Mail-Adresse ein. Nach Versand des Einmalcodes können Sie Ihr neues Passwort direkt festlegen.
                </p>
                <Input
                  type="email"
                  value={resetForm.email}
                  onChange={(event) => setResetForm({ ...resetForm, email: event.target.value })}
                  className="border-white/10 bg-white/5 text-white"
                  placeholder="Registrierte E-Mail-Adresse"
                  data-testid="login-reset-email-input"
                />
                {resetStep === 2 && (
                  <>
                    <Input
                      value={resetForm.code}
                      onChange={(event) => setResetForm({ ...resetForm, code: event.target.value })}
                      className="border-white/10 bg-white/5 text-white"
                      placeholder="Einmalcode"
                      data-testid="login-reset-code-input"
                    />
                    <Input
                      type="password"
                      value={resetForm.new_password}
                      onChange={(event) => setResetForm({ ...resetForm, new_password: event.target.value })}
                      className="border-white/10 bg-white/5 text-white"
                      placeholder="Neues Passwort"
                      data-testid="login-reset-new-password-input"
                    />
                  </>
                )}
                <div className="flex flex-wrap gap-3">
                  {resetStep === 1 ? (
                    <Button type="button" className="rounded-full bg-white text-slate-950 hover:bg-slate-200" onClick={requestReset} disabled={resetLoading} data-testid="login-reset-request-button">
                      {resetLoading ? "Versand läuft ..." : "Einmalcode anfordern"}
                    </Button>
                  ) : (
                    <Button type="button" className="rounded-full bg-white text-slate-950 hover:bg-slate-200" onClick={confirmReset} disabled={resetLoading} data-testid="login-reset-confirm-button">
                      {resetLoading ? "Wird geprüft ..." : "Neues Passwort speichern"}
                    </Button>
                  )}
                </div>
              </div>
            )}
            <Button className="w-full rounded-full bg-[#e5d3b3] text-[#121212] hover:bg-[#f4e7d3]" disabled={loading} data-testid="login-submit-button">
              <KeyRound className="mr-2 h-4 w-4" /> {loading ? "Anmeldung läuft ..." : "Login starten"}
            </Button>
          </form>
        </motion.div>
      </div>
    </MainLayout>
  );
}
